import React, { useState, useEffect, useCallback } from 'react'
import { Box, Grid } from '@material-ui/core'
import FormatListNumberedIcon from '@material-ui/icons/FormatListNumbered'
import DOMPurify from 'dompurify'

import { ERRORS } from 'consts'
import { useApi, useApiCall } from 'hooks'
import { snackbar } from 'components'
import { ConfigUtils } from 'utils/config'

import { DefaultTable } from '../../components'
import { useStyles } from './styles'
import { getMemberships } from '../../Memberships/api'
import { GetMembershipsResponse, Membership } from '../../Memberships/types'
import { Prize } from '../../Prizes/types'
import { Coupon } from '../../Coupons/types'
import { Tag } from '../../Tags/types'
import { getMembershipPrizes, getMembershipCoupons } from './api'
import { GetMembershipPrizesResponse, GetMembershipCouponsResponse } from './types'
import { OrderMembershipPrizesDialog } from '../../Memberships/components/OrderMembershipPrizesDialog'

const DAYS_MAP: Record<number, string> = {
  0: 'D',
  1: 'L',
  2: 'M',
  3: 'Mi',
  4: 'J',
  5: 'V',
  6: 'S',
}

const formatDays = (allowedDays?: number[]): string => {
  if (!allowedDays || allowedDays.length === 0) {
    return 'Todos los días'
  }

  if (allowedDays.length === 7) {
    return 'L, M, Mi, J, V, S, D'
  }

  const sortedDays = [...allowedDays].sort((a, b) => a - b)
  const dayLabels = sortedDays.map(day => DAYS_MAP[day])
  return dayLabels.join(', ')
}

const formatTimeRange = (startTime?: string, endTime?: string): string => {
  if (!startTime || !endTime) {
    return '-'
  }
  const formatTime = (time: string): string => {
    const [hours, minutes] = time.split(':')
    return `${hours}:${minutes}`
  }
  return `${formatTime(startTime)} a ${formatTime(endTime)} h`
}

const formatTags = (tags?: Tag[]): string => {
  if (!tags || tags.length === 0) {
    return '-'
  }
  return tags.map(tag => tag.title).join(', ')
}

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    style={{ padding: '20px 0', height: '100%', overflow: 'hidden' }}
  >
    {value === index && <Box style={{ height: '100%' }}>{children}</Box>}
  </div>
)

TabPanel.defaultProps = {
  children: undefined,
}

const { modules } = ConfigUtils
const isPromosModuleEnabled = modules.promos.enabled

const Preview: React.FC = () => {
  const classes = useStyles()
  const [{ data: memberships }] = useApi<GetMembershipsResponse, Membership[]>(
    getMemberships,
    response => response?.memberships ?? [],
    { baseData: [], onError: () => snackbar.show(ERRORS.GENERIC_ERROR_MESSAGE) },
  )

  const [activeLevelTab, setActiveLevelTab] = useState(0)
  const [prizesByMembership, setPrizesByMembership] = useState<Record<number, Prize[]>>({})
  const [couponsByMembership, setCouponsByMembership] = useState<Record<number, Coupon[]>>({})
  const [loadingPrizes, setLoadingPrizes] = useState<Record<number, boolean>>({})
  const [loadingCoupons, setLoadingCoupons] = useState<Record<number, boolean>>({})
  const [isOrderPrizesDialogOpen, setIsOrderPrizesDialogOpen] = useState(false)
  const [membershipToOrderPrizes, setMembershipToOrderPrizes] = useState<Membership | null>(null)

  const [getPrizesCall] = useApiCall<number, GetMembershipPrizesResponse>(getMembershipPrizes)
  const [getCouponsCall] = useApiCall<number, GetMembershipCouponsResponse>(getMembershipCoupons)

  const loadPrizes = useCallback(
    async (membershipId: number) => {
      if (prizesByMembership[membershipId]) return

      setLoadingPrizes(prev => ({ ...prev, [membershipId]: true }))
      try {
        const data = await getPrizesCall(membershipId)
        setPrizesByMembership(prev => ({ ...prev, [membershipId]: data.prizes || [] }))
      } catch (error) {
        snackbar.show(ERRORS.GENERIC_ERROR_MESSAGE)
      } finally {
        setLoadingPrizes(prev => ({ ...prev, [membershipId]: false }))
      }
    },
    [prizesByMembership, getPrizesCall],
  )

  const reloadPrizes = useCallback(
    async (membershipId: number) => {
      setLoadingPrizes(prev => ({ ...prev, [membershipId]: true }))
      try {
        const data = await getPrizesCall(membershipId)
        setPrizesByMembership(prev => ({ ...prev, [membershipId]: data.prizes || [] }))
      } catch (error) {
        snackbar.show(ERRORS.GENERIC_ERROR_MESSAGE)
      } finally {
        setLoadingPrizes(prev => ({ ...prev, [membershipId]: false }))
      }
    },
    [getPrizesCall],
  )

  const loadCoupons = useCallback(
    async (membershipId: number) => {
      if (couponsByMembership[membershipId]) return

      setLoadingCoupons(prev => ({ ...prev, [membershipId]: true }))
      try {
        const data = await getCouponsCall(membershipId)
        setCouponsByMembership(prev => ({ ...prev, [membershipId]: data.coupons || [] }))
      } catch (error) {
        snackbar.show(ERRORS.GENERIC_ERROR_MESSAGE)
      } finally {
        setLoadingCoupons(prev => ({ ...prev, [membershipId]: false }))
      }
    },
    [couponsByMembership, getCouponsCall],
  )

  useEffect(() => {
    if (memberships.length > 0 && activeLevelTab < memberships.length) {
      const membership = memberships[activeLevelTab]
      loadPrizes(membership.id)
      if (isPromosModuleEnabled) {
        loadCoupons(membership.id)
      }
    }
  }, [memberships, activeLevelTab, loadPrizes, loadCoupons])

  const handleOrderPrizes = () => {
    if (memberships.length > 0 && activeLevelTab < memberships.length) {
      const membership = memberships[activeLevelTab]
      const prizes = prizesByMembership[membership.id] || []

      const prizesForDialog = prizes.map(prize => ({
        id: prize.id,
        title: prize.title,
        order: prize.order || 0,
      }))

      const membershipWithPrizes = {
        ...membership,
        prizes: prizesForDialog,
      }
      setMembershipToOrderPrizes(membershipWithPrizes)
      setIsOrderPrizesDialogOpen(true)
    }
  }

  const handleOrderPrizesDone = async (updatedMembership: Membership) => {
    if (updatedMembership.id) {
      await reloadPrizes(updatedMembership.id)
    }
    setIsOrderPrizesDialogOpen(false)
    setMembershipToOrderPrizes(null)
  }

  const handleOrderPrizesDialogClose = () => {
    setIsOrderPrizesDialogOpen(false)
    setMembershipToOrderPrizes(null)
  }

  const prizeColumns = [
    {
      title: 'Título',
      field: 'title',
      render: (row: Prize) => (
        // eslint-disable-next-line react/no-danger
        <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(row.title) }} />
      ),
    },
    {
      title: 'Tags',
      render: (row: Prize) => formatTags(row.tags),
    },
    {
      title: 'Días',
      render: (row: Prize) => formatDays(row.allowedDays),
    },
    {
      title: 'Horarios',
      render: (row: Prize) => formatTimeRange(row.startTime, row.endTime),
    },
  ]

  const couponColumns = [
    {
      title: 'Título',
      field: 'title',
    },
    {
      title: 'Tags',
      render: (row: Coupon) => formatTags(row.tags),
    },
    {
      title: 'Días',
      render: (row: Coupon) => formatDays(row.allowedDays),
    },
    {
      title: 'Horarios',
      render: (row: Coupon) => formatTimeRange(row.startTime, row.endTime),
    },
  ]

  if (memberships.length === 0) {
    return <div style={{ padding: '20px' }}>No hay niveles disponibles</div>
  }

  const sortedMemberships = [...memberships].sort((a, b) => (a.level || 0) - (b.level || 0))

  return (
    <div className={classes.container}>
      <div className="mb-4">
        <div className="flex gap-8 border-b border-gray-200">
          {sortedMemberships.map((membership, index) => (
            <button
              key={membership.id}
              type="button"
              className={`pb-2 text-base font-semibold transition-colors duration-200 ${
                activeLevelTab === index
                  ? 'text-[#1E1E1E] border-b-2 border-[#1E1E1E]'
                  : 'text-gray-500 border-b-2 border-transparent hover:text-[#1E1E1E]'
              }`}
              style={{ fontFamily: 'Montserrat, sans-serif' }}
              onClick={() => setActiveLevelTab(index)}
            >
              Nivel {membership.level || index + 1}
            </button>
          ))}
        </div>
      </div>

      {sortedMemberships.map((membership, index) => {
        const hasCoupons =
          couponsByMembership[membership.id] && couponsByMembership[membership.id].length > 0
        const isLoadingCouponsData = loadingCoupons[membership.id] || false
        const shouldShowCouponsTable = isPromosModuleEnabled && (hasCoupons || isLoadingCouponsData)
        const isCurrentTab = activeLevelTab === index

        const prizeActions = isCurrentTab
          ? [
              {
                icon: () => <FormatListNumberedIcon />,
                tooltip: 'Ordenar premios',
                isFreeAction: true,
                onClick: handleOrderPrizes,
              },
            ]
          : []

        return (
          <TabPanel key={membership.id} value={activeLevelTab} index={index}>
            <Grid container spacing={3} style={{ height: '100%', flex: 1 }}>
              <Grid
                item
                xs={12}
                md={shouldShowCouponsTable ? 6 : 12}
                className={classes.tableContainer}
              >
                <div className={classes.tableWrapper}>
                  <DefaultTable
                    title={`Beneficios del Nivel ${membership.level || index + 1}`}
                    columns={prizeColumns}
                    data={prizesByMembership[membership.id] || []}
                    isLoading={loadingPrizes[membership.id] || false}
                    actions={prizeActions}
                    options={{
                      pageSize: 8,
                      search: true,
                      toolbar: true,
                    }}
                  />
                </div>
              </Grid>
              {shouldShowCouponsTable && (
                <Grid item xs={12} md={6} className={classes.tableContainer}>
                  <div className={classes.tableWrapper}>
                    <DefaultTable
                      title={`Cupones del Nivel ${membership.level || index + 1}`}
                      columns={couponColumns}
                      data={couponsByMembership[membership.id] || []}
                      isLoading={loadingCoupons[membership.id] || false}
                      options={{
                        pageSize: 8,
                        search: true,
                        toolbar: true,
                      }}
                    />
                  </div>
                </Grid>
              )}
            </Grid>
          </TabPanel>
        )
      })}
      {isOrderPrizesDialogOpen && membershipToOrderPrizes && (
        <OrderMembershipPrizesDialog
          membership={membershipToOrderPrizes}
          handleClose={handleOrderPrizesDialogClose}
          onDone={handleOrderPrizesDone}
        />
      )}
    </div>
  )
}

export { Preview }
